import { Box, Card, IconButton, Stack, Typography } from '@mui/material';
import { type Person, usePermissions } from '@tyro/api';
import { DateDropdownPicker, LoadingPlaceholderContainer } from '@tyro/core';
import { Trans, useTranslation } from '@tyro/i18n';
import { FullScreenIcon } from '@tyro/icons';
import { StudentAvatar } from '@tyro/people';
import { Link } from '@tyro/router/components';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useRunReports } from '../../api/run-report';
import { useReportFormatValues } from '../../hooks/use-report-format-values';
import { Report, getReportUrl } from '../../utils/get-report-url';
import type { ReportChipValue } from '../types';
import { ReportWidgetNoAccess } from './no-access';

export function BehaviourWidget() {
  const { t } = useTranslation(['common', 'reports']);
  const [date, setDate] = useState(dayjs());
  const dateString = date.format('YYYY-MM-DD');
  const { isStaffUserWithPermission } = usePermissions();
  const hasAccess = isStaffUserWithPermission('ps:1:notes:read_behaviour');

  const { data: behaviourData, isLoading } = useRunReports(
    {
      topReportId: Report.STUDENT_BEHAVIOUR,
      filter: {
        reportId: Report.STUDENT_BEHAVIOUR,
        limit: 5,
        filters: [
          {
            filterId: 'from_date',
            filterValue: dateString,
          },
          {
            filterId: 'to_date',
            filterValue: dateString,
          },
        ],
      },
    },
    {
      enabled: hasAccess,
    },
  );

  const { mapField } = useReportFormatValues({});

  const behaviourStudents = mapField<{
    id: number;
    avatar_cell: Person & {
      aenFlag: boolean;
      priorityFlag: boolean;
      medicalFlag: string;
    };
    party_id: number;
    class: string;
    created_by: string;
    tags: ReportChipValue[];
    category: ReportChipValue[];
    date: string;
  }>(behaviourData);

  return (
    <Card
      variant="soft"
      sx={{
        flex: 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        pl={1}
        mb={1}
      >
        <Typography variant="h6" component="span">
          {t('reports:behaviour')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DateDropdownPicker
            date={date}
            onChangeDate={setDate}
            ButtonProps={{
              size: 'small',
            }}
          />
          <IconButton
            component={Link}
            to={getReportUrl({
              report: Report.STUDENT_BEHAVIOUR,
              filters: {
                from_date: date,
                to_date: date,
              },
            })}
          >
            <FullScreenIcon
              sx={{ width: 20, height: 20, color: 'primary.main' }}
            />
          </IconButton>
        </Stack>
      </Stack>
      {isLoading || behaviourStudents.length === 0 ? (
        <Card
          sx={{
            minHeight: 160,
          }}
        >
          <LoadingPlaceholderContainer isLoading={isLoading}>
            <ReportWidgetNoAccess hasAccess={hasAccess}>
              <Stack
                sx={{
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                spacing={1}
              >
                <img
                  src="/assets/emojis/+1.png"
                  role="presentation"
                  alt="Thumbs up"
                  width="48"
                />
                <Typography
                  variant="body1"
                  component="span"
                  color="text.secondary"
                >
                  {t('reports:noBehaviourEventsToday')}
                </Typography>
              </Stack>
            </ReportWidgetNoAccess>
          </LoadingPlaceholderContainer>
        </Card>
      ) : (
        <Stack spacing={1.25}>
          {behaviourStudents.map(
            ({
              id,
              avatar_cell: studentPerson,
              party_id: partyId,
              class: classGroup,
              created_by: createdBy,
              tags,
              category,
              date: loggedDate,
            }) => {
              const name = studentPerson?.textValue;
              const { aenFlag, priorityFlag, medicalFlag, avatarUrl } =
                studentPerson?.typedValue ?? {};

              const loggedTime = loggedDate?.typedValue
                ? dayjs(loggedDate.typedValue).format('LT')
                : '-';

              return (
                <Card
                  component={Link}
                  key={id?.textValue ?? ''}
                  to={`/people/students/${partyId?.textValue ?? ''}/behaviour`}
                  sx={{
                    p: 1.5,
                    textDecoration: 'inherit',
                    '&:hover': {
                      bgcolor: 'indigo.100',
                    },
                    '&:active': {
                      bgcolor: 'indigo.200',
                    },
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box onClick={(e) => e.preventDefault()}>
                          <StudentAvatar
                            partyId={partyId?.typedValue ?? 0}
                            name={name ?? ''}
                            src={avatarUrl}
                            isPriorityStudent={!!priorityFlag}
                            hasSupportPlan={!!aenFlag}
                            hasMedical={!!medicalFlag}
                            person={studentPerson?.typedValue}
                            size={48}
                          />
                        </Box>
                        <Stack>
                          <Typography variant="subtitle1" component="span">
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            component="span"
                          >
                            {classGroup?.textValue}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        textAlign="right"
                      >
                        <Trans ns="reports" i18nKey="loggedAtTimeByStaff">
                          Logged at {{ loggedTime }} <br />
                          by{' '}
                          <Typography
                            variant="body2"
                            component="span"
                            fontWeight="600"
                            color="text.primary"
                          >
                            {/* @ts-expect-error */}
                            {{ createdBy: createdBy?.textValue }}
                          </Typography>
                        </Trans>
                      </Typography>
                    </Stack>
                    <Stack direction="row" flex={1} spacing={0.75}>
                      {category?.renderedValue}
                      {tags?.renderedValue}
                    </Stack>
                  </Stack>
                </Card>
              );
            },
          )}
        </Stack>
      )}
    </Card>
  );
}
